import {
  Link,
  Multiselect,
  MultiselectProps,
  Select,
  SelectProps,
  SpaceBetween,
  Button,
  StatusIndicator,
  Input,
} from '@amzn/awsui-components-react';
import { AttendanceStatus, Shift, ShiftType } from '@amzn/red-velvet-api';
import { useTranslation } from 'react-i18next';
import { AttendanceIndicator } from '../../../utils/attendance/peopleSearch';
import { formatAttendanceTimeDisplay } from '../../../utils/attendance/time';
import { useTimezone } from '../../../hooks/timezone';
import { asDateTimeString } from '../../../utils/dateFormatting';

export interface MultiShiftAttendee {
  name: string;
  alias: string;
  signedUpShifts: Shift[];
  alreadyTrackedShiftIds: string[]; // Shift IDs that are already tracked (excluded from dropdown)
}

export interface ValidAttendee {
  name: string;
  alias: string;
  shift: Shift;
  isLocked: boolean; // true if this is an existing participation (user already signed up)
  alreadyTrackedShiftIds: string[]; // Shift IDs that are already tracked (excluded from dropdown)
}

export interface AlreadyTrackedAttendeeRow {
  name: string;
  alias: string;
  shiftId: string | undefined;
  shift: Shift | undefined;
  attendanceStatus: AttendanceStatus | undefined;
  attendanceMinutes: number | undefined;
}

export interface FailedIdentifier {
  id: number;
  correctedValue: string;
  originalValue: string;
  error: string;
  input: string;
  isEditing: boolean;
  isResolved: boolean;
  isValidating: boolean;
}

export function useMultiShiftAttendeeColumns(
  shiftOptions: MultiselectProps.Options,
  shifts: Shift[],
  onShiftChange: (alias: string, options: MultiselectProps.Options) => void
) {
  const { t, i18n } = useTranslation(['attendance', 'translation']);
  const timezone = useTimezone();

  return [
    {
      cell: (item: MultiShiftAttendee) => item.name,
      header: t('bulkUpload.validation.tableColumns.name'),
      id: 'name',
    },
    {
      cell: (item: MultiShiftAttendee) => (
        <Link
          target="_blank"
          href={`https://phonetool.amazon.com/users/${item.alias}`}
          variant="primary"
        >
          {item.alias}
        </Link>
      ),
      header: t('bulkUpload.validation.tableColumns.alias'),
      id: 'alias',
    },
    {
      cell: (item: MultiShiftAttendee) => {
        // Filter out already-tracked shifts from dropdown options for this user
        const alreadyTrackedShifts = new Set(item.alreadyTrackedShiftIds || []);
        const filteredOptions = shiftOptions.filter(
          (option) => !alreadyTrackedShifts.has(option.value as string)
        );

        return (
          <Multiselect
            expandToViewport
            filteringType="auto"
            inlineTokens
            options={filteredOptions}
            selectedOptions={shifts
              .filter(
                (s) =>
                  s.shiftType !== ShiftType.WAITLIST &&
                  item.signedUpShifts.map((shift) => shift.shiftId).includes(s.shiftId)
              )
              .map((shift: Shift) => ({
                label: shift.name,
                value: shift.shiftId,
                description:
                  shift.startDateTime &&
                  asDateTimeString(shift.startDateTime, timezone, i18n.language),
              }))}
            tokenLimit={3}
            onChange={({ detail }) => onShiftChange(item.alias, [...detail.selectedOptions])}
          />
        );
      },
      header: (
        <SpaceBetween alignItems="center" direction="horizontal" size="xs">
          {t('bulkUpload.validation.tableColumns.shift')}
        </SpaceBetween>
      ),
      id: 'shift',
    },
  ];
}

export function useValidAttendeeColumns(
  shiftOptions: SelectProps.Options,
  shifts: Shift[],
  isUnlistedEventMode = false,
  eventTitle?: string
) {
  const { t, i18n } = useTranslation(['attendance', 'translation']);
  const timezone = useTimezone();

  return [
    {
      cell: (item: ValidAttendee) => item.name,
      header: t('bulkUpload.validation.tableColumns.name'),
      id: 'name',
    },
    {
      cell: (item: ValidAttendee) => (
        <Link
          target="_blank"
          href={`https://phonetool.amazon.com/users/${item.alias}`}
          variant="primary"
        >
          {item.alias}
        </Link>
      ),
      header: t('bulkUpload.validation.tableColumns.alias'),
      id: 'alias',
    },
    {
      cell: (item: ValidAttendee) => (isUnlistedEventMode ? eventTitle || '' : item.shift.name),
      header: (
        <SpaceBetween alignItems="center" direction="horizontal" size="xs">
          {t('bulkUpload.validation.tableColumns.shift')}
        </SpaceBetween>
      ),
      id: 'shift',
      // Not editable when in unlisted event mode
      ...(isUnlistedEventMode
        ? {}
        : {
            editConfig: {
              ariaLabel: t('bulkUpload.validation.tableColumns.shift'),
              editIconAriaLabel: t('bulkUpload.validation.editShift'),
              errorIconAriaLabel: 'Error',
              editingCell: (
                item: ValidAttendee,
                {
                  setValue,
                  currentValue,
                }: { setValue: (value: Shift) => void; currentValue?: Shift }
              ) => {
                // Filter out already-tracked shifts
                const alreadyTrackedShifts = new Set(item.alreadyTrackedShiftIds || []);
                const filteredOptions = shiftOptions.filter(
                  (option) => !alreadyTrackedShifts.has(option.value as string)
                );

                return (
                  <Select
                    autoFocus
                    expandToViewport
                    filteringType="auto"
                    options={filteredOptions}
                    selectedOption={
                      currentValue
                        ? {
                            label: currentValue.name,
                            value: currentValue.shiftId,
                            description:
                              currentValue.startDateTime &&
                              asDateTimeString(currentValue.startDateTime, timezone, i18n.language),
                          }
                        : {
                            label: item.shift.name,
                            value: item.shift.shiftId,
                            description:
                              item.shift.startDateTime &&
                              asDateTimeString(item.shift.startDateTime, timezone, i18n.language),
                          }
                    }
                    onChange={({ detail }) => {
                      if (detail.selectedOption?.value) {
                        const selectedShift = shifts.find(
                          (s) => s.shiftId === detail.selectedOption?.value
                        );
                        if (selectedShift) {
                          setValue(selectedShift);
                        }
                      }
                    }}
                  />
                );
              },
              disabledReason: (item: ValidAttendee) =>
                item.isLocked ? t('bulkUpload.validation.lockedParticipation') : undefined,
            },
          }),
    },
  ];
}

/**
 * Returns read-only column definitions for the skipped/already-tracked tab.
 * Displays one row per shift for volunteers whose attendance has already been tracked (immutable to coordinators).
 */
export function useAlreadyTrackedShiftColumns() {
  const { t } = useTranslation(['attendance', 'translation']);

  return [
    {
      cell: (item: AlreadyTrackedAttendeeRow) => item.name,
      header: t('bulkUpload.validation.tableColumns.name'),
      id: 'name',
    },
    {
      cell: (item: AlreadyTrackedAttendeeRow) => (
        <Link
          target="_blank"
          href={`https://phonetool.amazon.com/users/${item.alias}`}
          variant="primary"
        >
          {item.alias}
        </Link>
      ),
      header: t('bulkUpload.validation.tableColumns.alias'),
      id: 'alias',
    },
    {
      cell: (item: AlreadyTrackedAttendeeRow) => item.shift?.name || '',
      header: t('bulkUpload.validation.tableColumns.shift'),
      id: 'shift',
    },
    {
      cell: (item: AlreadyTrackedAttendeeRow) => {
        if (!item.attendanceStatus) return '';
        return <AttendanceIndicator status={item.attendanceStatus} />;
      },
      header: t('bulkUpload.validation.tableColumns.attendance'),
      id: 'attendance',
    },
    {
      cell: (item: AlreadyTrackedAttendeeRow) => {
        if (!item.attendanceMinutes || item.attendanceMinutes === 0) return '';
        return formatAttendanceTimeDisplay(item.attendanceMinutes, t);
      },
      header: t('bulkUpload.validation.tableColumns.trackedTime'),
      id: 'trackedTime',
    },
  ];
}

export function useInvalidEntryColumns(
  onCorrectedValueChange: (id: number, value: string) => void,
  onAcceptCorrection: (id: number) => void,
  onCancelEditItem: (id: number) => void,
  onEditItem: (id: number) => void
) {
  const { t } = useTranslation(['attendance', 'translation']);

  return [
    {
      cell: (item: FailedIdentifier) => <>{item.input}</>,
      header: t('bulkUpload.validation.tableHeaders.yourInput'),
      id: 'input',
    },
    {
      cell: (item: FailedIdentifier) => (
        <StatusIndicator type={item.isResolved ? 'success' : 'error'}>{item.error}</StatusIndicator>
      ),
      header: t('bulkUpload.validation.tableHeaders.validationError'),
      id: 'error',
    },
    {
      cell: (item: FailedIdentifier) => (
        <SpaceBetween alignItems="center" direction="horizontal" size="xs">
          <Input
            value={item.correctedValue}
            onChange={({ detail }) => onCorrectedValueChange(item.id, detail.value)}
            readOnly={!item.isEditing || item.isResolved}
          />
          {/* Display edit button only if not yet resolved or when user is not already editting. */}
          {!item.isEditing && !item.isResolved && (
            <Button
              iconName="edit"
              variant="inline-icon"
              onClick={() => onEditItem(item.id)}
              ariaLabel={t('bulkUpload.validation.ariaLabels.editItem', {
                input: item.input,
              })}
            />
          )}
          {/* If editing, show close and check buttons */}
          {item.isEditing && !item.isResolved && (
            <>
              <Button
                iconName="close"
                variant="inline-icon"
                onClick={() => onCancelEditItem(item.id)}
                disabled={item.isValidating}
                ariaLabel={t('bulkUpload.validation.ariaLabels.cancelEdit', {
                  input: item.input,
                })}
              />
              <Button
                iconName="check"
                variant="inline-icon"
                onClick={() => onAcceptCorrection(item.id)}
                loading={item.isValidating}
                disabled={item.isValidating}
                ariaLabel={t('bulkUpload.validation.ariaLabels.acceptCorrection', {
                  input: item.input,
                })}
              />
            </>
          )}
        </SpaceBetween>
      ),
      header: t('bulkUpload.validation.tableHeaders.correctedValue'),
      id: 'corrected',
    },
  ];
}
